import { get, post, put, del } from '../request';
import {
  LoginResponse, AccountDetail, Order, PaginatedResponse,
  AdminStats, Card, SystemSettings, ApiResponse, OrderAnalytics,
  Item, AIReplySettings, ShippingRule, ReplyRule, DefaultReply, AccountRuntimeStatus, AccountVerificationStatus,
  HandoffNotificationRules, HandoffNotificationRuleInput, HandoffNotificationRecord
} from '../types';

// Auth
export const login = async (data: { username?: string; password?: string; email?: string; verification_code?: string }): Promise<LoginResponse> => {
  return post('/login', data);
};

export const verifySession = async (): Promise<{ authenticated: boolean; initialized?: boolean; user_id?: number; username?: string; is_admin?: boolean }> => {
  return get('/verify');
};

export const logout = async (): Promise<ApiResponse> => {
  return post('/logout', {});
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<ApiResponse> => {
  return post('/change-password', { current_password: currentPassword, new_password: newPassword });
};

export type UpdateCheck = {
  supported: boolean;
  available: boolean;
  current_version?: string;
  latest_version?: string;
  release_notes?: string;
  created_at?: string;
  message?: string;
};

export type UpdateStatus = {
  supported: boolean;
  result: string;
  stage?: string;
  message?: string;
  current_version?: string;
  target_version?: string;
  updated_at?: string;
};

export const checkUpdate = async (): Promise<UpdateCheck> => get('/admin/update/check');
export const getUpdateStatus = async (): Promise<UpdateStatus> => get('/admin/update/status');
export const startUpdate = async (): Promise<{ accepted: boolean; job_id: string; target_version: string }> => post('/admin/update/start', {});

// Official TMC channel.  These models deliberately omit credentials, OAuth
// grants and source payloads: the administrator UI only needs readiness and
// manual-work-item metadata.
export type OfficialTmcAccount = {
  id: number;
  provider: 'official_tmc' | string;
  platform_account_id: string;
  display_name: string;
  enabled: boolean;
  authorization_state: OfficialTmcReadinessState;
  authorized_at?: string | null;
  last_checked_at?: string | null;
  last_error_code?: string;
};

export type OfficialTmcEvent = {
  id: number;
  provider: 'official_tmc' | string;
  message_id: string;
  content_id?: string;
  topic: string;
  platform_account_id: string;
  received_at?: string;
  processing_status: 'pending' | 'handed_off' | 'confirmed' | 'failed' | string;
  consumed_at?: string | null;
  handed_off_at?: string | null;
  error_code?: string;
};

export type OfficialTmcReadinessState =
  | 'unconfigured'
  | 'awaiting_authorization'
  | 'ready'
  | 'unavailable_topic'
  | 'authorization_expired'
  | 'credential_error'
  | 'failed_check'
  | string;

export type OfficialTmcProviderStatus = {
  state?: OfficialTmcReadinessState;
  enabled?: boolean;
  allowed_topics?: string[];
};

type OfficialTmcStatusResponse = {
  provider?: OfficialTmcProviderStatus | 'official_tmc' | string;
  state?: OfficialTmcReadinessState;
  enabled?: boolean;
  allowed_topics?: string[];
  callback_url?: string;
  account?: OfficialTmcAccount;
  accounts?: OfficialTmcAccount[];
  events?: OfficialTmcEvent[];
  last_check?: {
    state?: OfficialTmcReadinessState;
    checked_at?: string;
    error_code?: string;
  };
};

export type OfficialTmcStatus = {
  provider?: 'official_tmc' | string;
  state: OfficialTmcReadinessState;
  enabled: boolean;
  configured?: boolean;
  allowed_topics: string[];
  callback_url?: string;
  account?: OfficialTmcAccount;
  accounts?: OfficialTmcAccount[];
  events?: OfficialTmcEvent[];
  last_check?: {
    state?: OfficialTmcReadinessState;
    checked_at?: string;
    error_code?: string;
  };
};

export type OfficialTmcReadinessResult = {
  checked?: boolean;
  state: OfficialTmcReadinessState;
  allowed_topics: string[];
  message?: string;
  checked_at?: string;
  error_code?: string;
};

export type OfficialTmcConsumeResult = {
  received: number;
  persisted: number;
  deduplicated: number;
  confirmed: number;
  rejected: number;
  failed: number;
  message?: string;
};

const normalizeOfficialTmcStatus = (raw: OfficialTmcStatusResponse): OfficialTmcStatus => {
  const nestedProvider = typeof raw.provider === 'object' && raw.provider !== null ? raw.provider : undefined;
  const account = raw.account;
  return {
    ...raw,
    provider: typeof raw.provider === 'string' ? raw.provider : 'official_tmc',
    state: nestedProvider?.state || raw.state || 'unconfigured',
    enabled: Boolean(nestedProvider?.enabled ?? raw.enabled),
    allowed_topics: nestedProvider?.allowed_topics || raw.allowed_topics || [],
    account,
    accounts: raw.accounts || (account ? [account] : []),
    events: raw.events || [],
  };
};

export const getOfficialTmcStatus = async (): Promise<OfficialTmcStatus> => normalizeOfficialTmcStatus(await get<OfficialTmcStatusResponse>('/official-tmc/status'));
export const runOfficialTmcReadinessCheck = async (): Promise<OfficialTmcReadinessResult> => post('/official-tmc/readiness-check', {});
export const upsertOfficialTmcAccount = async (data: {
  platform_account_id: string;
  display_name?: string;
  enabled?: boolean;
}): Promise<{ account: OfficialTmcAccount; message?: string }> => post('/official-tmc/accounts', data);
export const getOfficialTmcAuthorizationUrl = async (): Promise<{ authorization_url: string }> => get('/official-tmc/oauth/authorize');
export const consumeOfficialTmcOnce = async (): Promise<OfficialTmcConsumeResult> => post('/official-tmc/consume-once', {});
export const handoffOfficialTmcEvent = async (eventId: number): Promise<{ event: OfficialTmcEvent; message?: string }> => post(`/official-tmc/events/${eventId}/handoff`, {});

// Accounts
export const getAccountDetails = async (): Promise<AccountDetail[]> => {
  const data = await get<any[]>('/cookies/details');
  return data.map(item => ({
    id: item.id,
    value: '',
    cookie: '',
    enabled: item.enabled,
    auto_confirm: item.auto_confirm,
    remark: item.remark,
    note: item.remark,
    pause_duration: item.pause_duration,
    username: item.username || '',
    login_password: '',
    show_browser: item.show_browser,
    nickname: item.nickname || item.remark || `账号 ${item.id.substring(0,6)}...`,
    avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${item.id}`,
    ai_enabled: false,
  }));
};

export const generateQRLogin = async (): Promise<{ success: boolean; session_id?: string; qr_code_url?: string }> => {
  return post('/qr-login/generate');
};

export const checkQRLoginStatus = async (sessionId: string): Promise<any> => {
  return get(`/qr-login/check/${sessionId}`);
};

export const updateAccountStatus = async (id: string, enabled: boolean): Promise<any> => {
  return put(`/cookies/${id}/status`, { enabled });
};

export const deleteAccount = async (id: string): Promise<any> => {
  return del(`/cookies/${id}`);
};

export const updateAccountRemark = async (id: string, remark: string): Promise<any> => {
  return put(`/cookies/${id}/remark`, { remark });
};

export const updateAccountAutoConfirm = async (id: string, autoConfirm: boolean): Promise<any> => {
  return put(`/cookies/${id}/auto-confirm`, { auto_confirm: autoConfirm });
};

export const updateAccountPauseDuration = async (id: string, pauseDuration: number): Promise<any> => {
  return put(`/cookies/${id}/pause-duration`, { pause_duration: pauseDuration });
};

export const updateAccountCookie = async (id: string, value: string): Promise<any> => {
  return put(`/cookies/${id}`, { id, value });
};

export const updateAccountLoginInfo = async (id: string, data: {
  username?: string;
  login_password?: string;
  show_browser?: boolean;
}): Promise<any> => {
  return put(`/cookies/${id}/login-info`, data);
};

export const getAllAISettings = async (): Promise<Record<string, AIReplySettings>> => {
  return get('/ai-reply-settings');
};

export const getAccountRuntimeStatuses = async (): Promise<Record<string, AccountRuntimeStatus>> => {
  return get('/account-runtime-status');
};

export const getAccountVerificationStatus = async (id: string): Promise<AccountVerificationStatus> => {
  return get(`/account-verification/${id}`);
};

export const startAccountVerification = async (id: string): Promise<AccountVerificationStatus> => {
  return post(`/account-verification/${id}/start`);
};

export const issueAccountVerificationBridgeTicket = async (id: string): Promise<{ success: boolean; ticket: string; expires_in: number }> => {
  return post(`/account-verification/${id}/bridge-ticket`);
};

const LOCAL_VERIFICATION_BRIDGE = 'http://127.0.0.1:17890';

type LocalVerificationBridgePayload = {
  available?: boolean;
  state?: string;
  success?: boolean;
  detail?: string;
  message?: string;
};

export const assertLocalVerificationBridgeSuccess = (
  payload: LocalVerificationBridgePayload,
  responseOk: boolean,
): void => {
  const unavailable = payload.available === false || payload.state === 'unavailable';
  if (!responseOk || unavailable || payload.success === false) {
    throw new Error(payload.detail || payload.message || 'Windows 验证桥接服务不可用。');
  }
};

const callLocalVerificationBridge = async <T>(path: string, ticket: string): Promise<T> => {
  const response = await fetch(`${LOCAL_VERIFICATION_BRIDGE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticket }),
  });
  const payload = await response.json().catch(() => ({})) as LocalVerificationBridgePayload;
  assertLocalVerificationBridgeSuccess(payload, response.ok);
  return payload as T;
};

export const startLocalAccountVerificationBridge = async (ticket: string): Promise<AccountVerificationStatus> => {
  return callLocalVerificationBridge<AccountVerificationStatus>('/verification/start', ticket);
};

export const completeLocalAccountVerificationBridge = async (ticket: string): Promise<{ success: boolean; message: string }> => {
  return callLocalVerificationBridge<{ success: boolean; message: string }>('/verification/complete', ticket);
};

export const cancelLocalAccountVerificationBridge = async (ticket: string): Promise<{ success: boolean }> => {
  return callLocalVerificationBridge<{ success: boolean }>('/verification/cancel', ticket);
};

export const requestAccountVerificationChallenge = async (id: string): Promise<{ success: boolean; result: string; message: string }> => {
  return post(`/account-verification/${id}/request`, {});
};

export const completeAccountVerification = async (id: string): Promise<{ success: boolean; message: string }> => {
  return post(`/account-verification/${id}/complete`);
};

export const resumeAccountVerification = async (id: string): Promise<{ success: boolean; resumed: boolean; message: string }> => {
  return post(`/account-verification/${id}/resume`);
};

export const reconnectAccountAfterAuthorization = async (id: string): Promise<{ success: boolean; resumed: boolean; message: string }> => {
  return post(`/account-reconnect/${id}`);
};

export const cancelAccountVerification = async (id: string): Promise<{ success: boolean }> => {
  return post(`/account-verification/${id}/cancel`);
};

export const getAIModelCredentialStatus = async (): Promise<{ configured: boolean; source: 'web' | 'environment' | 'none' }> => {
  return get('/ai-model-credential');
};

export const updateAIModelCredential = async (apiKey: string): Promise<{ message: string }> => {
  return put('/ai-model-credential', { api_key: apiKey });
};

// Orders
export const getOrders = async (
  cookieId?: string,
  status?: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<Order>> => {
  const params: any = { page, page_size: pageSize };
  if (cookieId) params.cookie_id = cookieId;
  if (status && status !== 'all') params.status = status;

  const res = await get<any>('/api/orders', params);

  // Handle backend response variations
  const orders = res.orders || res.data || [];
  return {
    success: true,
    data: orders,
    total: res.total || orders.length,
    page: res.page || page,
    page_size: res.page_size || pageSize,
    total_pages: res.total_pages || 1
  };
};

export const getOrderDetail = async (orderId: string): Promise<{ success: boolean; data?: Order }> => {
  const result = await get<{ order?: Order; data?: Order }>(`/api/orders/${orderId}`);
  return {
    success: true,
    data: result.order || result.data
  };
};

export const updateOrder = async (orderId: string, data: Partial<Order>): Promise<ApiResponse> => {
  return put(`/api/orders/${orderId}`, data);
};

export const deleteOrder = async (orderId: string): Promise<ApiResponse> => {
  return del(`/api/orders/${orderId}`);
};

export const syncOrders = async (cookieId?: string, status?: string): Promise<any> => {
  const formData = new FormData();
  if (cookieId) formData.append('cookie_id', cookieId);
  if (status) formData.append('status', status);

  // 使用 fetch 来发送 FormData（Cookie 会话，自动携带凭证）
  const response = await fetch('/api/orders/refresh', {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  return response.json();
};

export const syncSingleOrder = async (orderId: string): Promise<any> => {
  return post(`/api/orders/${orderId}/refresh`);
};

export const manualShipOrder = async (orderIds: string[], shipMode: 'status_only' | 'full_delivery', content?: string): Promise<any> => {
    return post('/api/orders/manual-ship', {
        order_ids: orderIds,
        ship_mode: shipMode,
        custom_content: content
    });
}

export const importOrders = async (data: Partial<Order>[] | FormData): Promise<any> => {
  const isFormData = data instanceof FormData;
  const response = await fetch('/api/orders/import', {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    },
    body: isFormData ? data : JSON.stringify(data)
  });
  return response.json();
}

// Stats
export const getAdminStats = async (): Promise<AdminStats> => {
  return get('/admin/stats');
};

export const getOrderAnalytics = async (daysOrParams: number | {start_date: string; end_date: string} = 7): Promise<OrderAnalytics> => {
    let params: {start_date: string; end_date: string};

    if (typeof daysOrParams === 'number') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - daysOrParams);
        params = {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
        };
    } else {
        params = daysOrParams;
    }

    return get('/analytics/orders', params);
}

export const getValidOrders = async (dateRange: {start_date: string; end_date: string}): Promise<Order[]> => {
    const res = await get<any>('/analytics/orders/valid', {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
    });
    return res.orders || [];
}

// Cards
export const getCards = async (): Promise<Card[]> => {
  const res = await get<any>('/cards');
  return Array.isArray(res) ? res : (res.cards || []);
};

export const createCard = async (data: Partial<Card>): Promise<{ id: number; message: string }> => {
  return post('/cards', data);
};

export const updateCard = async (cardId: string, data: Partial<Card>): Promise<ApiResponse> => {
  return put(`/cards/${cardId}`, data);
};

export const deleteCard = async (cardId: string): Promise<ApiResponse> => {
  return del(`/cards/${cardId}`);
};

export const getCardDetails = async (cardId: string): Promise<any> => {
  return get(`/cards/${cardId}/details`);
};

// Items
export const getItems = async (): Promise<Item[]> => {
    const res = await get<any>('/items');
    const items = Array.isArray(res) ? res : (res.items || []);
    return items.map((item: any) => ({
      ...item,
      is_multi_qty_ship: item.is_multi_qty_ship ?? item.multi_quantity_delivery ?? false,
    }));
}

export const syncItemsFromAccount = async (cookieId: string): Promise<any> => {
    return post('/items/get-all-from-account', { cookie_id: cookieId });
}

export interface ItemSyncProgress {
  completed: number;
  total: number;
  currentAccountId: string | null;
  successCount: number;
  failedCount: number;
}

export interface ItemSyncBatchResult {
  total: number;
  successCount: number;
  failedCount: number;
  failedAccounts: Array<{ cookieId: string; message: string }>;
}

export const syncItemsFromAccounts = async (
  cookieIds: string[],
  onProgress?: (progress: ItemSyncProgress) => void,
): Promise<ItemSyncBatchResult> => {
  const result: ItemSyncBatchResult = {
    total: cookieIds.length,
    successCount: 0,
    failedCount: 0,
    failedAccounts: [],
  };

  for (const cookieId of cookieIds) {
    onProgress?.({
      completed: result.successCount + result.failedCount,
      total: result.total,
      currentAccountId: cookieId,
      successCount: result.successCount,
      failedCount: result.failedCount,
    });

    try {
      const response = await syncItemsFromAccount(cookieId);
      if (response?.success === false) {
        throw new Error(response.message || '同步商品失败');
      }
      result.successCount += 1;
    } catch (error) {
      result.failedCount += 1;
      result.failedAccounts.push({
        cookieId,
        message: error instanceof Error ? error.message : '同步商品失败',
      });
    }

    onProgress?.({
      completed: result.successCount + result.failedCount,
      total: result.total,
      currentAccountId: cookieId,
      successCount: result.successCount,
      failedCount: result.failedCount,
    });
  }

  onProgress?.({
    completed: result.total,
    total: result.total,
    currentAccountId: null,
    successCount: result.successCount,
    failedCount: result.failedCount,
  });

  return result;
}

export const deleteItem = async (cookieId: string, itemId: string): Promise<any> => {
    return del(`/items/${cookieId}/${itemId}`);
}

export const createItem = async (cookieId: string, data: any): Promise<any> => {
    return post(`/items/${cookieId}`, data);
}

export const updateItem = async (cookieId: string, itemId: string, data: any): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}`, data);
}

export const updateItemMultiSpec = async (cookieId: string, itemId: string, enabled: boolean): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}/multi-spec`, { is_multi_spec: enabled });
};

export const updateItemMultiQty = async (cookieId: string, itemId: string, enabled: boolean): Promise<any> => {
    return put(`/items/${cookieId}/${itemId}/multi-quantity-delivery`, { multi_quantity_delivery: enabled });
};

// Rules - 发货规则 (使用正确的后端API)
export const getShippingRules = async (): Promise<ShippingRule[]> => {
    const res = await get<any>('/delivery-rules');
    const rules = Array.isArray(res) ? res : (res.data || res.rules || []);
    // 转换后端数据格式到前端格式
    return rules.map((item: any) => ({
        id: String(item.id),
        name: item.description || item.keyword || '',
        item_keyword: item.keyword || '',
        card_group_id: item.card_id || 0,
        card_group_name: item.card_name || '',
        priority: item.delivery_count || 1,
        enabled: item.enabled || false
    }));
}

export const updateShippingRule = async (rule: Partial<ShippingRule>): Promise<any> => {
    const payload = {
        keyword: rule.item_keyword,
        card_id: rule.card_group_id,
        delivery_count: rule.priority,
        enabled: rule.enabled ?? true,
        description: rule.name
    };
    return rule.id ? put(`/delivery-rules/${rule.id}`, payload) : post('/delivery-rules', payload);
}

export const deleteShippingRule = async (id: string): Promise<any> => del(`/delivery-rules/${id}`);

// Rules - 关键词回复规则 (使用关键词API)
export const getReplyRules = async (cookieId?: string): Promise<ReplyRule[]> => {
    if (!cookieId) return [];
    const res = await get<any>(`/keywords-with-item-id/${cookieId}`);
    const keywords = Array.isArray(res) ? res : [];
    return keywords.map((item: any, index: number) => ({
        id: String(index),
        keyword: item.keyword || '',
        reply_content: item.reply || '',
        match_type: 'exact' as const,
        enabled: true
    }));
}

export const updateReplyRule = async (rule: Partial<ReplyRule>, cookieId: string): Promise<any> => {
    // 获取现有关键词
    const existing = await get<any>(`/keywords-with-item-id/${cookieId}`);
    const keywords = Array.isArray(existing) ? existing : [];

    // 更新或添加关键词
    if (rule.id) {
        const index = parseInt(rule.id);
        if (index >= 0 && index < keywords.length) {
            keywords[index] = {
                keyword: rule.keyword,
                reply: rule.reply_content,
                item_id: ''
            };
        }
    } else {
        keywords.push({
            keyword: rule.keyword,
            reply: rule.reply_content,
            item_id: ''
        });
    }

    return post(`/keywords-with-item-id/${cookieId}`, { keywords });
}

export const deleteReplyRule = async (id: string, cookieId: string): Promise<any> => {
    const existing = await get<any>(`/keywords-with-item-id/${cookieId}`);
    const keywords = Array.isArray(existing) ? existing : [];
    const index = parseInt(id);
    if (index >= 0 && index < keywords.length) {
        keywords.splice(index, 1);
    }
    return post(`/keywords-with-item-id/${cookieId}`, { keywords });
}

// Settings
export const getSystemSettings = async (): Promise<SystemSettings> => {
    const res = await get<{data: SystemSettings}>('/system-settings');
    const settings = res.data || res; // handle {success:true, data: {...}} wrapper if exists
    const booleanKeys = new Set([
      'registration_enabled', 'show_default_login_info', 'login_captcha_enabled',
      'item_sync_enabled', 'auto_reply_enabled'
    ]);
    const numericKeys = new Set(['item_sync_interval', 'item_sync_max_pages', 'smtp_port']);
    return Object.fromEntries(Object.entries(settings).map(([key, value]) => {
      if (booleanKeys.has(key)) return [key, value === true || value === 'true'];
      if (numericKeys.has(key)) return [key, Number(value)];
      return [key, value];
    })) as SystemSettings;
};

export const updateSystemSettings = async (settings: Partial<SystemSettings>): Promise<ApiResponse> => {
    // API expects individual PUTs, but we'll loop in the service for convenience or assume bulk endpoint if updated
    // Based on docs 12.2, we iterate.
    const promises = Object.entries(settings).map(([key, value]) => {
         return put(`/system-settings/${key}`, { value: String(value) });
    });
    await Promise.all(promises);
    return { success: true, message: 'Settings saved' };
};

export const getAccountAISettings = async (cookieId: string): Promise<AIReplySettings> => {
    return get(`/ai-reply-settings/${cookieId}`);
}

export const updateAccountAISettings = async (cookieId: string, settings: Partial<AIReplySettings>): Promise<ApiResponse> => {
  const payload = {
    ai_enabled: settings.ai_enabled ?? false,
    model_name: settings.model_name?.trim() ?? '',
    base_url: settings.base_url?.trim() ?? '',
    max_discount_percent: settings.max_discount_percent ?? 10,
    max_discount_amount: settings.max_discount_amount ?? 100,
    max_bargain_rounds: settings.max_bargain_rounds ?? 3,
    custom_prompts: settings.custom_prompts ?? ''
  };
  return put(`/ai-reply-settings/${cookieId}`, payload);
}

export const testAIConnection = async (cookieId: string): Promise<ApiResponse> => {
  const result = await post<{ success?: boolean; message?: string; reply?: string }>(`/ai-reply-test/${cookieId}`, {
    message: '你好，这是一条测试消息',
  });
  if (result.reply) {
    return { success: true, message: `AI 回复: ${result.reply}` };
  }
  return { success: result.success ?? true, message: result.message || 'AI 连接测试成功' };
}

// Notification Channels
export const getNotificationChannels = async (): Promise<{ success: boolean; data?: any[] }> => {
  const result = await get<any[]>('/notification-channels');
  const channels = (result || []).map((item: any) => {
    let parsedConfig;
    try {
      parsedConfig = JSON.parse(item.config);
    } catch {
      parsedConfig = undefined;
    }
    return {
      id: String(item.id),
      name: item.name,
      type: item.type,
      config: parsedConfig,
      enabled: item.enabled,
      created_at: item.created_at,
      updated_at: item.updated_at,
    };
  });
  return { success: true, data: channels };
}

export const createNotificationChannel = async (data: { name: string; type: string; config: Record<string, unknown> }): Promise<ApiResponse> => {
  return post('/notification-channels', {
    ...data,
    config: JSON.stringify(data.config)
  });
}

export const updateNotificationChannel = async (channelId: string, data: { name?: string; config?: Record<string, unknown>; enabled?: boolean }): Promise<ApiResponse> => {
  const payload: Record<string, unknown> = { ...data };
  if ('config' in data) {
    payload.config = JSON.stringify(data.config);
  }
  return put(`/notification-channels/${channelId}`, payload);
}

export const deleteNotificationChannel = async (channelId: string): Promise<ApiResponse> => {
  return del(`/notification-channels/${channelId}`);
}

// Message Notifications
export const getMessageNotifications = async (): Promise<{ success: boolean; data?: any[] }> => {
  const result = await get<Record<string, any[]>>('/message-notifications');
  const notifications = [];
  for (const [cookieId, channelList] of Object.entries(result || {})) {
    if (Array.isArray(channelList)) {
      for (const item of channelList) {
        notifications.push({
          cookie_id: cookieId,
          channel_id: item.channel_id,
          channel_name: item.channel_name,
          enabled: item.enabled,
        });
      }
    }
  }
  return { success: true, data: notifications };
}

export const setMessageNotification = async (cookieId: string, channelId: number, enabled: boolean): Promise<ApiResponse> => {
  return post(`/message-notifications/${cookieId}`, { channel_id: channelId, enabled });
}

export const deleteMessageNotification = async (notificationId: string): Promise<ApiResponse> => {
  return del(`/message-notifications/${notificationId}`);
}

export const deleteAccountNotifications = async (cookieId: string): Promise<ApiResponse> => {
  return del(`/message-notifications/account/${cookieId}`);
}

// Handoff notifications
export const getHandoffNotificationRules = async (): Promise<HandoffNotificationRules> => {
  return get('/handoff-notification-rules');
};

export const syncSystemAIPrompt = async (prompt: string, cookieIds: string[]): Promise<{
  success: boolean;
  synced_count: number;
  requested_count: number;
  failed_count: number;
}> => {
  return post('/system-settings/ai-prompt-sync', { prompt, cookie_ids: cookieIds });
};

export const updateGlobalHandoffNotificationRule = async (rule: HandoffNotificationRuleInput): Promise<ApiResponse> => {
  return put('/handoff-notification-rules/global', rule);
};

export const updateAccountHandoffNotificationRule = async (cookieId: string, rule: HandoffNotificationRuleInput): Promise<ApiResponse> => {
  return put(`/handoff-notification-rules/accounts/${cookieId}`, rule);
};

export const deleteAccountHandoffNotificationRule = async (cookieId: string): Promise<ApiResponse> => {
  return del(`/handoff-notification-rules/accounts/${cookieId}`);
};

export const testHandoffNotificationRule = async (scope: string): Promise<ApiResponse & { sent_at?: string; record_status?: string; scope?: string }> => {
  return post(`/handoff-notification-rules/${scope}/test`, {});
};

export const previewHandoffNotification = async (rule: HandoffNotificationRuleInput): Promise<{ payload: Record<string, unknown> }> => {
  return post('/handoff-notification-rules/preview', rule);
};

export const getHandoffNotificationRecords = async (): Promise<HandoffNotificationRecord[]> => {
  return get('/handoff-notification-records');
};

// Default Reply
export const getDefaultReplies = async (): Promise<Record<string, DefaultReply>> => {
  return get('/api/default-replies');
};

export const getDefaultReply = async (cookieId: string): Promise<DefaultReply> => {
  const result = await get<any>(`/api/default-reply/${cookieId}`);
  return {
    cookie_id: cookieId,
    enabled: result.enabled || false,
    reply_content: result.reply_content || '',
    reply_once: result.reply_once || false,
    reply_image_url: result.reply_image_url || '',
    reply_image_mode: result.reply_image_mode || 'off',
    reply_image_keywords: Array.isArray(result.reply_image_keywords) ? result.reply_image_keywords : [],
    reply_image_version: result.reply_image_version || 1,
  };
};

export const updateDefaultReply = async (cookieId: string, data: Partial<DefaultReply>): Promise<ApiResponse> => {
  return put(`/api/default-reply/${cookieId}`, {
    enabled: data.enabled ?? false,
    reply_content: data.reply_content || '',
    reply_once: data.reply_once ?? false,
    reply_image_url: data.reply_image_url || '',
    reply_image_mode: data.reply_image_mode || 'off',
    reply_image_keywords: data.reply_image_keywords || [],
  });
};

export const deleteDefaultReply = async (cookieId: string): Promise<ApiResponse> => {
  return del(`/api/default-reply/${cookieId}`);
};

export const clearDefaultReplyRecords = async (cookieId: string): Promise<ApiResponse> => {
  return post(`/api/default-reply/${cookieId}/clear-records`, {});
};

export const uploadReplyImage = async (image: File): Promise<{ image_url: string }> => {
  const formData = new FormData();
  formData.append('image', image);
  const response = await fetch('/upload-image', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.detail || payload.message || `上传失败: ${response.status}`);
  }
  return payload;
};
